import React, { useState } from 'react';
import { Checkbox } from '../../../Components/Checkbox';
import { Dropdown } from '../../../Components/Dropdown';
import { Input } from '../../../Components/Input';
import { Button } from '../../../Components/Button';
import { Text } from '../../../Components/Typography';
import { PrecioCopia } from '../../../hooks/usePreciosCopias';
import './PriceFilters.scss';

interface PriceFiltersProps {
  precios: PrecioCopia[];
  filters: {
    selectedColors: string[];
    selectedPapers: string[];
    selectedSizes: string[];
    selectedFaz: string[];
    searchTerm: string;
  };
  onFiltersChange: (filters: any) => void;
  onClearFilters: () => void;
}

export const PriceFilters: React.FC<PriceFiltersProps> = ({
  precios,
  filters,
  onFiltersChange,
  onClearFilters
}) => {
  // Estado para controlar la expansión de filtros
  const [expandedFilters, setExpandedFilters] = useState<{
    papers: boolean;
    colors: boolean;
    sizes: boolean;
    faz: boolean;
  }>({
    papers: false,
    colors: false,
    sizes: false,
    faz: false
  });

  // Extraer opciones únicas de los precios
  const uniqueColors = Array.from(new Set(precios.map(p => p.color_name)));
  const uniquePapers = Array.from(new Set(precios.map(p => p.papel_name)));
  const uniqueSizes = Array.from(new Set(precios.map(p => p.hoja_name)));
  const uniqueFaz = Array.from(new Set(precios.map(p => p.faz_name)));

  const handleColorChange = (color: string, checked: boolean) => {
    const newColors = checked 
      ? [...filters.selectedColors, color]
      : filters.selectedColors.filter(c => c !== color);
    
    onFiltersChange({
      ...filters,
      selectedColors: newColors
    });
  };

  const handlePaperChange = (paper: string, checked: boolean) => {
    const newPapers = checked 
      ? [...filters.selectedPapers, paper]
      : filters.selectedPapers.filter(p => p !== paper);
    
    onFiltersChange({
      ...filters,
      selectedPapers: newPapers
    });
  };

  const handleSizeChange = (size: string, checked: boolean) => {
    const newSizes = checked 
      ? [...filters.selectedSizes, size]
      : filters.selectedSizes.filter(s => s !== size);
    
    onFiltersChange({
      ...filters,
      selectedSizes: newSizes
    });
  };

  const handleFazChange = (faz: string, checked: boolean) => {
    const newFaz = checked 
      ? [...filters.selectedFaz, faz]
      : filters.selectedFaz.filter(f => f !== faz);
    
    onFiltersChange({
      ...filters,
      selectedFaz: newFaz
    });
  };

  const handleSearchChange = (value: string) => {
    onFiltersChange({
      ...filters,
      searchTerm: value
    });
  };

  const hasActiveFilters = () => {
    return filters.selectedColors.length > 0 ||
           filters.selectedPapers.length > 0 ||
           filters.selectedSizes.length > 0 ||
           filters.selectedFaz.length > 0 ||
           filters.searchTerm.trim() !== '';
  };

  const toggleFilterExpansion = (filterType: 'papers' | 'colors' | 'sizes' | 'faz') => {
    setExpandedFilters(prev => ({
      ...prev,
      [filterType]: !prev[filterType]
    }));
  };

  return (
    <div className="price-filters">
      <div className="filters-header">
        <div className="header-left">
          <Text bold>🔍 Filtros</Text>
          {hasActiveFilters() && (
            <span className="active-count">
              ({filters.selectedColors.length + filters.selectedPapers.length + filters.selectedSizes.length + filters.selectedFaz.length + (filters.searchTerm ? 1 : 0)})
            </span>
          )}
        </div>
        {hasActiveFilters() && (
          <Button onClick={onClearFilters} outlined small>
            🗑️ Limpiar
          </Button>
        )}
      </div>

      <div className="filters-content">
        {/* Búsqueda */}
        <div className="search-section">
          <Input
            placeholder="🔍 Buscar precios..."
            value={filters.searchTerm}
            onChange={handleSearchChange}
          />
        </div>

        {/* Filtros rápidos */}
        <div className="quick-filters">
          {/* Color */}
          <div className="filter-group">
            <div className="filter-label">
              <Text bold>Color</Text>
              <span className="count">({filters.selectedColors.length}/{uniqueColors.length})</span>
            </div>
            <div className="filter-options">
              {uniqueColors.slice(0, expandedFilters.colors ? undefined : 4).map(color => (
                <button
                  key={color}
                  className={`filter-option ${filters.selectedColors.includes(color) ? 'active' : ''}`}
                  onClick={() => handleColorChange(color, !filters.selectedColors.includes(color))}
                >
                  {color}
                </button>
              ))}
              {uniqueColors.length > 4 && (
                <button 
                  className="filter-option more"
                  onClick={() => toggleFilterExpansion('colors')}
                >
                  {expandedFilters.colors ? 'Ver menos' : `+${uniqueColors.length - 4} más`}
                </button>
              )}
            </div>
          </div>

          {/* Papel */}
          <div className="filter-group">
            <div className="filter-label">
              <Text bold>Papel</Text>
              <span className="count">({filters.selectedPapers.length}/{uniquePapers.length})</span>
            </div>
            <div className="filter-options">
              {uniquePapers.slice(0, expandedFilters.papers ? undefined : 4).map(paper => (
                <button
                  key={paper}
                  className={`filter-option ${filters.selectedPapers.includes(paper) ? 'active' : ''}`}
                  onClick={() => handlePaperChange(paper, !filters.selectedPapers.includes(paper))}
                >
                  {paper}
                </button>
              ))}
              {uniquePapers.length > 4 && (
                <button 
                  className="filter-option more"
                  onClick={() => toggleFilterExpansion('papers')}
                >
                  {expandedFilters.papers ? 'Ver menos' : `+${uniquePapers.length - 4} más`}
                </button>
              )}
            </div>
          </div>

          {/* Tamaño */}
          <div className="filter-group">
            <div className="filter-label">
              <Text bold>Tamaño</Text>
              <span className="count">({filters.selectedSizes.length}/{uniqueSizes.length})</span>
            </div>
            <div className="filter-options">
              {uniqueSizes.slice(0, expandedFilters.sizes ? undefined : 4).map(size => (
                <button
                  key={size}
                  className={`filter-option ${filters.selectedSizes.includes(size) ? 'active' : ''}`}
                  onClick={() => handleSizeChange(size, !filters.selectedSizes.includes(size))}
                >
                  {size}
                </button>
              ))}
              {uniqueSizes.length > 4 && (
                <button 
                  className="filter-option more"
                  onClick={() => toggleFilterExpansion('sizes')}
                >
                  {expandedFilters.sizes ? 'Ver menos' : `+${uniqueSizes.length - 4} más`}
                </button>
              )}
            </div>
          </div>

          {/* Faz */}
          <div className="filter-group">
            <div className="filter-label">
              <Text bold>Faz</Text>
              <span className="count">({filters.selectedFaz.length}/{uniqueFaz.length})</span>
            </div>
            <div className="filter-options">
              {uniqueFaz.slice(0, expandedFilters.faz ? undefined : 4).map(faz => (
                <button
                  key={faz}
                  className={`filter-option ${filters.selectedFaz.includes(faz) ? 'active' : ''}`}
                  onClick={() => handleFazChange(faz, !filters.selectedFaz.includes(faz))}
                >
                  {faz}
                </button>
              ))}
              {uniqueFaz.length > 4 && (
                <button 
                  className="filter-option more"
                  onClick={() => toggleFilterExpansion('faz')}
                >
                  {expandedFilters.faz ? 'Ver menos' : `+${uniqueFaz.length - 4} más`}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
